import { inject, Injectable, OnDestroy } from '@angular/core';
import { concatMap, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import {
  CiscoPhoneNumberReport,
  IgnoredEntry,
  JobState,
  JobStatus,
  SiteEntry,
  TeamsNumberEntry,
  TeamsUpnEntry,
} from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { delayWhen, filter, map, take, tap } from 'rxjs/operators';
import { FileDownloadService } from '../../../shared/services/file-download.service';
import { MigrationsCucmNumberReportPollingContext } from './migrations-cucm-number-report-polling.context';

@Injectable()
export class MigrationsCucmNumberReportContext implements OnDestroy {
  private _http = inject(HttpClient);
  private _route = inject(ActivatedRoute);
  private _migrationsCucmNumberReportPollingContext = inject(MigrationsCucmNumberReportPollingContext);
  private _fileDownloadService = inject(FileDownloadService);

  private _stateSource = new ReplaySubject<CiscoPhoneNumberReport>(1);
  state$ = this._stateSource.asObservable();

  private _hasData = false;

  private _jobStateSource = new ReplaySubject<JobStatus>(1);
  jobState$ = this._jobStateSource.asObservable();

  private _baseUrl = `/services/microsoft/migrations/tenants/${this._route.snapshot.params.tenantId}/exports/cisco/${this._route.snapshot.params.id}/number-report`;

  constructor() {
    this._init().subscribe();
  }

  ngOnDestroy(): void {
    this._migrationsCucmNumberReportPollingContext.stopPolling();
  }

  refresh(): Observable<void> {
    return this._refresh().pipe(switchMap(() => this._init()));
  }

  hasData(): boolean {
    return this._hasData;
  }

  exportDirectoryNumbersReport(): Observable<void> {
    return this._fileDownloadService.downloadFile(`${this._baseUrl}/export`);
  }

  updateDirectoryNumberUpn(rowId: string, updatedUpn: string): Observable<void> {
    const body: TeamsUpnEntry = {
      teamsUpn: updatedUpn,
    };

    return this._http.put<void>(`${this._baseUrl}/teams-upn/${rowId}`, body).pipe(delayWhen(() => this.softRefresh()));
  }

  updateDirectoryNumberTeamsNumber(rowId: string, teamsNumber: string): Observable<void> {
    const body: TeamsNumberEntry = {
      teamsNumber,
    };
    return this._http
      .put<void>(`${this._baseUrl}/teams-numbers/${rowId}`, body)
      .pipe(delayWhen(() => this.softRefresh()));
  }

  updateDirectoryNumberSiteOverride(rowId: string, siteId: string): Observable<void> {
    const body: SiteEntry = {
      siteId,
    };

    return this._http
      .put<void>(`${this._baseUrl}/site-management/site-override/row-id/${rowId}`, body)
      .pipe(delayWhen(() => this.softRefresh()));
  }

  removeDirectoryNumberSiteOverride(rowId: string): Observable<void> {
    return this._http
      .delete<void>(`${this._baseUrl}/site-management/site-override/row-id/${rowId}`)
      .pipe(delayWhen(() => this.softRefresh()));
  }

  updateIgnoredDirectoryNumbers(ignoredEntries: IgnoredEntry[]): Observable<void> {
    const body: { ignoredEntries: IgnoredEntry[] } = {
      ignoredEntries,
    };

    return this._http.put<void>(`${this._baseUrl}/ignored-entries`, body).pipe(delayWhen(() => this.softRefresh()));
  }

  private _init(): Observable<void> {
    this._migrationsCucmNumberReportPollingContext.startPolling();

    return this._migrationsCucmNumberReportPollingContext.state$.pipe(
      tap((status) => this._jobStateSource.next(status)),
      filter((status) => status.jobState === JobState.IDLE),
      concatMap((status: JobStatus) => {
        if (!status.lastRunTime) {
          return this._refresh();
        }
        this._migrationsCucmNumberReportPollingContext.stopPolling();
        if (!status.error) {
          return this._get();
        }
        return of({
          entries: [],
          mostRecentCdrTimestamp: null,
          oldestCdrTimestamp: null,
          totalNumberOfCdrs: 0,
        });
      }),
      filter((report) => !!report),
      map((report: CiscoPhoneNumberReport) => {
        this._publish(report);
      }),
      take(1),
    );
  }

  private _get(): Observable<CiscoPhoneNumberReport> {
    return this._http.get<CiscoPhoneNumberReport>(this._baseUrl);
  }

  softRefresh(): Observable<void> {
    return this._get().pipe(
      map((report) => {
        this._publish(report);
      }),
    );
  }

  private _publish(report: CiscoPhoneNumberReport): void {
    this._hasData = true;
    this._stateSource.next(report);
  }

  private _refresh(): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/refresh`, null);
  }
}
